import React, { useState } from "react";
import { AsYouType, isValidPhoneNumber } from 'libphonenumber-js';
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

function Background() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      {/* Base celeste/azul */}
      <div
        className="absolute inset-0"
        style={{
          background: "linear-gradient(180deg, #edf7ff 0%, #e2f0ff 38%, #d4e9ff 100%)",
        }}
      />
      {/* Manchas radiales para profundidad */}
      <div
        className="absolute inset-0"
        style={{
          background:
            `radial-gradient(700px 460px at 15% 85%, rgba(56,189,248,0.28), transparent 60%),
             radial-gradient(640px 420px at 85% 20%, rgba(59,130,246,0.22), transparent 62%),
             radial-gradient(520px 380px at 50% 55%, rgba(99,102,241,0.18), transparent 65%)`,
          filter: "saturate(110%)",
        }}
      />
      {/* Sheen sutil */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(120deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0) 30%, rgba(255,255,255,0) 70%, rgba(255,255,255,0.35) 100%)",
          mixBlendMode: "overlay",
        }}
      />
      {/* Vignette para legibilidad */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(1100px 500px at 50% 100%, rgba(8,47,73,0.10), transparent 62%)",
        }}
      />
      {/* Textura muy sutil */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: "radial-gradient(rgba(2,6,23,0.35) 0.5px, transparent 0.5px)",
          backgroundSize: "12px 12px",
        }}
      />
    </div>
  );
}

export default function PreLanding() {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const isValidInternationalMobile = (raw) => {
    const s = (raw || '').trim();
    if (!s) return false;
    // Si viene con +, validamos E.164
    if (s.startsWith('+')) return isValidPhoneNumber(s);
    // Si no, asumimos CL como país por defecto (permite locales sin prefijo)
    return isValidPhoneNumber(s, 'CL');
  };

  const formatInternationalPhone = (raw) => {
    const ayt = new AsYouType('CL'); // por defecto CL para locales sin prefijo
    return ayt.input(raw || '');
  };

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = "Ingresa tu nombre";
    if (!phone.trim()) {
      e.phone = "Ingresa tu celular";
    } else if (!isValidInternationalMobile(phone)) {
      e.phone = "Ingresa un celular válido (ej: +56 9 1234 5678 o +1 202 555 0123)";
    }
    if (!email.trim()) {
      e.email = "Ingresa tu correo";
    } else {
      const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
      if (!ok) e.email = "Correo inválido";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    try {
      setSubmitting(true);
      const res = await fetch('/api/prelanding', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre: name.trim(), telefono: phone.trim(), correo: email.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setErrors((prev) => ({ ...prev, server: data.error || 'No se pudo guardar, intenta nuevamente.' }));
        return;
      }
      navigate('/landing');
    } catch (err) {
      setErrors((prev) => ({ ...prev, server: 'Error de red, intenta nuevamente.' }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center">
      <Background />
      <div className="w-[92%] max-w-[760px]">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-[28px] border border-sky-100/60 bg-white/70 backdrop-blur-xl shadow-xl p-8 md:p-12 text-center"
        >
          <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-br from-sky-600 via-blue-600 to-indigo-600 select-none drop-shadow-[0_1px_0_rgba(255,255,255,0.35)]">
            FINTEGRAM
          </h1>

          {!showForm && (
            <>
              <p className="mt-5 text-base md:text-lg text-slate-600">
                ¿Estás listo para conocer nuestra propuesta?
              </p>
              <div className="mt-8">
                <button
                  type="button"
                  onClick={() => setShowForm(true)}
                  className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700 text-white h-12 px-8 text-base font-medium shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sky-600"
                >
                  Comenzar
                </button>
              </div>
            </>
          )}

          {showForm && (
            <form onSubmit={onSubmit} noValidate className="mt-8 grid gap-4 text-left">
              <div>
                <label className="block text-sm text-slate-700 mb-1">Nombre</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={`w-full h-11 rounded-xl border px-3 bg-white/80 focus:outline-none focus:ring-2 focus:ring-sky-500 ${errors.name ? 'border-red-400' : 'border-slate-200'}`}
                  placeholder="Tu nombre"
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'error-name' : undefined}
                />
                {errors.name && <div id="error-name" className="mt-1 text-xs text-red-600">{errors.name}</div>}
              </div>
              <div>
                <label className="block text-sm text-slate-700 mb-1">Número</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(formatInternationalPhone(e.target.value))}
                  className={`w-full h-11 rounded-xl border px-3 bg-white/80 focus:outline-none focus:ring-2 focus:ring-sky-500 ${errors.phone ? 'border-red-400' : 'border-slate-200'}`}
                  placeholder="Ej: +56 9 1234 5678"
                  aria-invalid={!!errors.phone}
                  aria-describedby={errors.phone ? 'error-phone' : undefined}
                />
                {errors.phone && <div id="error-phone" className="mt-1 text-xs text-red-600">{errors.phone}</div>}
              </div>
              <div>
                <label className="block text-sm text-slate-700 mb-1">Correo</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full h-11 rounded-xl border px-3 bg-white/80 focus:outline-none focus:ring-2 focus:ring-sky-500 ${errors.email ? 'border-red-400' : 'border-slate-200'}`}
                  placeholder="tu@correo.com"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'error-email' : undefined}
                />
                {errors.email && <div id="error-email" className="mt-1 text-xs text-red-600">{errors.email}</div>}
              </div>

              <div className="pt-2">
                {errors.server && <div className="mb-3 text-xs text-red-600">{errors.server}</div>}
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center rounded-2xl bg-slate-900 disabled:opacity-60 hover:bg-slate-800 text-white h-11 px-6 text-sm font-medium shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-900"
                >
                  {submitting ? 'Guardando…' : 'Ir'}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}
